import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import { LayoutDashboard, FileStack, BookOpen, Gavel, ClipboardCheck, ShieldAlert, MessageSquare, Search, CheckCircle2, Send, Sparkles, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Checkbox } from "@/components/ui/checkbox";
import { REGULATIONS, AUDIT_FINDINGS, RISK_MATRIX, GOVERNANCE_ACTIONS, BOARD_COPILOT, BOARD_PACK_SECTIONS, SYNTHETIC_DISCLAIMER } from "@/data/synthetic";
import { toast } from "sonner";

const MENU = [
  { key: "board", label: "Board Dashboard", icon: LayoutDashboard },
  { key: "pack", label: "Board Pack Builder", icon: FileStack },
  { key: "regulatory", label: "Regulatory Intelligence", icon: BookOpen },
  { key: "audit", label: "Audit Intelligence", icon: Gavel },
  { key: "governance", label: "Governance Actions", icon: ClipboardCheck },
  { key: "risk", label: "Risk Heatmap", icon: ShieldAlert },
  { key: "copilot", label: "Executive Copilot", icon: MessageSquare },
  { key: "search", label: "Document Search", icon: Search },
  { key: "approvals", label: "Approval Workflow", icon: CheckCircle2 },
];

const AXIS_STYLE = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const TOOL_STYLE = { background: "#0F1424", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#E2E8F0", fontSize: 12 };

const Panel = ({ title, children }) => (
  <div className="glass rounded-xl p-5">
    <div className="tech-label mb-3">{title}</div>
    {children}
  </div>
);

function BoardDashboard() {
  const stats = [
    { l: "Open regulatory obligations", v: "26" }, { l: "High-risk audit findings", v: "3" }, { l: "Overdue actions", v: "1" }, { l: "KRIs breached", v: "4" }, { l: "Board decisions pending", v: "7" }, { l: "Compliance status", v: "94%" },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {stats.map((k) => (
          <div key={k.l} className="glass rounded-lg p-4"><div className="tech-label">{k.l}</div><div className="text-xl font-heading text-white mt-2">{k.v}</div></div>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="EMERGING RISK THEMES">
          <ul className="space-y-3">
            {[
              { t: "Cyber material incident disclosure", g: "Regulatory · OSC · Q1" },
              { t: "Third-party critical service concentration", g: "Operational resilience" },
              { t: "Climate disclosure baseline gap", g: "OSFI phase-in" },
              { t: "AML monitoring calibration", g: "Audit repeat finding" },
            ].map((x) => (
              <li key={x.t} className="border-l-2 border-violet-400/50 pl-3"><div className="text-sm text-slate-100">{x.t}</div><div className="text-[11px] text-slate-400 font-mono mt-0.5">{x.g}</div></li>
            ))}
          </ul>
        </Panel>
        <Panel title="OBLIGATIONS DUE THIS QUARTER">
          <div className="space-y-2">
            {REGULATIONS.slice(0, 4).map((r) => (
              <div key={r.id} className="border border-white/5 rounded-md p-3 bg-white/[0.02]">
                <div className="flex items-center justify-between"><span className="text-[10px] font-mono text-cyan-300">{r.regulator}</span><span className="text-[10px] font-mono text-slate-500">{r.effective}</span></div>
                <div className="text-sm text-slate-100 mt-1">{r.summary}</div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="DECISIONS PENDING BOARD">
          <ul className="space-y-2 text-sm text-slate-200">
            {["Approve refreshed AML thresholds", "Endorse climate disclosure plan", "Ratify third-party critical inventory", "Accept audit remediation timeline"].map((x) => <li key={x} className="border border-white/5 rounded-md p-3">{x}</li>)}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

function BoardPackBuilder() {
  const [sel, setSel] = useState(new Set(["financial", "risk", "compliance", "audit"]));
  const toggle = (k) => { const n = new Set(sel); n.has(k) ? n.delete(k) : n.add(k); setSel(n); };
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 glass rounded-xl p-5">
        <div className="tech-label mb-3">SECTIONS</div>
        <div className="space-y-2">
          {BOARD_PACK_SECTIONS.map((s) => (
            <label key={s.key} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${sel.has(s.key) ? "border-cyan-400/40 bg-cyan-500/5" : "border-white/5 hover:border-white/15"}`} data-testid={`pack-${s.key}`}>
              <Checkbox checked={sel.has(s.key)} onCheckedChange={() => toggle(s.key)} className="mt-1 border-white/20 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500" />
              <div>
                <div className="text-sm text-white font-medium">{s.title}</div>
                <div className="text-xs text-slate-400 mt-0.5">{s.desc}</div>
              </div>
            </label>
          ))}
        </div>
        <button onClick={() => toast.success("Board pack generation queued", { description: `${sel.size} sections · MapleTrust Bank` })} className="w-full mt-4 px-4 py-3 rounded-md text-sm btn-primary" data-testid="pack-generate">Generate board pack</button>
      </div>
      <div className="lg:col-span-3 glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="tech-label">PREVIEW · Q1 2026 · MAPLETRUST BOARD PACK</div>
          <div className="text-[10px] font-mono text-slate-500">{sel.size} sections · citations enforced</div>
        </div>
        <div className="rounded-lg bg-black/30 border border-white/5 p-5">
          <div className="font-heading text-white text-lg">Executive summary</div>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">Capital ratios remain within OSFI guidance. Two audit repeat findings persist in operations. AML monitoring recalibration in flight for effective 2026-03-15. Climate disclosure baseline enters planning; third-party critical inventory on track.</p>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {[...sel].map((k) => {
            const s = BOARD_PACK_SECTIONS.find((x) => x.key === k);
            return (
              <div key={k} className="rounded-md bg-white/[0.02] border border-white/5 p-3">
                <div className="tech-label">{s.title.toUpperCase()}</div>
                <div className="text-xs text-slate-300 mt-1 leading-relaxed">{s.desc}. Automated summary with source citations, page references and evidence links.</div>
                <div className="mt-2 text-[10px] font-mono text-cyan-300">SOURCES: 4–7 · CITATIONS: ENFORCED</div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => toast("Board pack sent for approval", { description: "CFO Office · Company Secretary" })} className="px-4 py-2 rounded-md text-sm btn-ghost" data-testid="pack-send">Send for approval</button>
          <button onClick={() => toast("PDF exported")} className="px-4 py-2 rounded-md text-sm btn-ghost">Export PDF</button>
        </div>
      </div>
    </div>
  );
}

function RegulatoryIntel() {
  return (
    <div className="glass rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5"><tr className="text-left text-slate-400">{["Circular", "Regulator", "Issued", "Effective", "Summary", "Departments", "Obligations", "Risk", "Owner", "Status"].map((h) => <th key={h} className="px-3 py-3 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {REGULATIONS.map((r) => (
              <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`reg-${r.id}`}>
                <td className="px-3 py-3 text-cyan-300 font-mono">{r.id}</td>
                <td className="px-3 py-3 text-slate-200">{r.regulator}</td>
                <td className="px-3 py-3 font-mono text-slate-400">{r.issued}</td>
                <td className="px-3 py-3 font-mono text-slate-200">{r.effective}</td>
                <td className="px-3 py-3 text-slate-200 max-w-xs">{r.summary}</td>
                <td className="px-3 py-3"><div className="flex flex-wrap gap-1">{r.depts.map((d) => <span key={d} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300">{d}</span>)}</div></td>
                <td className="px-3 py-3 font-mono">{r.obligations}</td>
                <td className="px-3 py-3"><span className={`text-[10px] font-mono ${r.risk === "High" ? "text-rose-300" : "text-amber-300"}`}>{r.risk}</span></td>
                <td className="px-3 py-3 text-slate-300">{r.owner}</td>
                <td className="px-3 py-3"><span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-400/20">{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditIntel() {
  return (
    <div className="space-y-3">
      {AUDIT_FINDINGS.map((f) => (
        <div key={f.id} className="glass rounded-xl p-5" data-testid={`aud-${f.id}`}>
          <div className="grid lg:grid-cols-12 gap-4">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-2">
                <span className="tech-label">{f.id}</span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${f.severity === "High" ? "bg-rose-500/15 text-rose-300" : f.severity === "Med" ? "bg-amber-500/15 text-amber-300" : "bg-slate-500/15 text-slate-300"}`}>{f.severity.toUpperCase()}</span>
                {f.repeat && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">REPEAT</span>}
              </div>
              <div className="font-heading text-white mt-1 tracking-tight">{f.finding}</div>
              <div className="text-xs text-slate-400 mt-1">Dept: {f.dept} · Owner: {f.owner}</div>
            </div>
            <div className="lg:col-span-4 rounded-md bg-black/25 border border-white/5 p-3">
              <div className="tech-label">ROOT CAUSE</div>
              <div className="text-xs text-slate-200 mt-1">{f.root}</div>
              <div className="mt-2 flex items-center gap-3 text-[11px]"><span className="tech-label">TREND</span><span className={`font-mono ${f.trend === "worsening" ? "text-rose-300" : f.trend === "improving" ? "text-emerald-300" : "text-slate-300"}`}>{f.trend.toUpperCase()}</span></div>
            </div>
            <div className="lg:col-span-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-white/[0.02] border border-white/5 p-2"><div className="tech-label">DUE</div><div className="text-sm font-mono text-white mt-1">{f.due}</div></div>
                <div className="rounded-md bg-white/[0.02] border border-white/5 p-2"><div className="tech-label">STATUS</div><div className={`text-sm font-mono mt-1 ${f.status === "Overdue" ? "text-rose-300" : "text-cyan-300"}`}>{f.status}</div></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function GovernanceActionsView() {
  return (
    <div className="glass rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5"><tr className="text-left text-slate-400">{["Action", "Owner", "Committee", "Due", "Status"].map((h) => <th key={h} className="px-4 py-3 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {GOVERNANCE_ACTIONS.map((g) => (
              <tr key={g.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`gov-${g.id}`}>
                <td className="px-4 py-3"><div className="text-slate-100">{g.action}</div><div className="text-[10px] font-mono text-slate-500">{g.id}</div></td>
                <td className="px-4 py-3 text-slate-300">{g.owner}</td>
                <td className="px-4 py-3 text-violet-300">{g.committee}</td>
                <td className="px-4 py-3 font-mono">{g.due}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded ${g.status === "Overdue" ? "bg-rose-500/10 text-rose-300" : g.status === "Planning" ? "bg-slate-500/10 text-slate-300" : "bg-cyan-500/10 text-cyan-300"}`}>{g.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RiskHeatmap() {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 glass rounded-xl p-5">
        <div className="tech-label mb-3">RISK BY CATEGORY · COUNT BY SEVERITY</div>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={RISK_MATRIX}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="category" tick={AXIS_STYLE} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_STYLE} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOL_STYLE} />
            <Bar dataKey="low" stackId="a" fill="#22C55E" />
            <Bar dataKey="med" stackId="a" fill="#F59E0B" />
            <Bar dataKey="high" stackId="a" fill="#F43F5E" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="glass rounded-xl p-5">
        <div className="tech-label mb-3">HEATMAP · LIKELIHOOD × IMPACT</div>
        <div className="grid grid-cols-5 gap-1">
          {Array.from({ length: 25 }).map((_, i) => {
            const row = Math.floor(i / 5), col = i % 5;
            const heat = row + col;
            const c = heat <= 2 ? "#153B2A" : heat <= 4 ? "#3D3312" : heat <= 6 ? "#4A1F1A" : "#6B1A2E";
            const val = ((row + 1) * (col + 1));
            return <div key={i} className="aspect-square flex items-center justify-center text-xs font-mono text-white/80" style={{ background: c, borderRadius: 4 }}>{val}</div>;
          })}
        </div>
        <div className="mt-3 flex justify-between text-[10px] font-mono text-slate-500"><span>Low likelihood</span><span>High likelihood</span></div>
      </div>
    </div>
  );
}

function ExecutiveCopilot() {
  const [msgs, setMsgs] = useState([
    { role: "user", text: BOARD_COPILOT.q },
    { role: "assistant", table: BOARD_COPILOT.answer, note: "Seeded response · illustrative synthetic data" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `xy-board-${Math.random().toString(36).slice(2, 10)}`);
  const API = process.env.REACT_APP_BACKEND_URL + "/api";

  const send = async (q) => {
    const query = (q || input).trim();
    if (!query || loading) return;
    setMsgs((m) => [...m, { role: "user", text: query }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/copilot/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: query, product: "board", session_id: sessionId }),
      });
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = await res.json();
      setMsgs((m) => [...m, { role: "assistant", ...data }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", root: "Unable to reach the live copilot right now. Please try again.", metrics: [], actions: [], sources: [], confidence: 0, refreshed: new Date().toISOString().slice(0, 16) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-4">
      <div className="lg:col-span-3 glass rounded-xl p-5 min-h-[500px] flex flex-col">
        <div className="tech-label flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-ring inline-block" />
          BOARD COPILOT · CLAUDE SONNET 5 · SOURCE-GROUNDED · HUMAN APPROVAL
        </div>
        <div className="mt-4 flex-1 space-y-4 overflow-y-auto max-h-[520px]">
          {msgs.map((m, i) => m.role === "user" ? (
            <div key={i} className="text-sm text-slate-200 bg-white/5 rounded-md p-3 border border-white/5">{m.text}</div>
          ) : m.table ? (
            <div key={i} className="rounded-md border border-violet-400/20 bg-violet-500/5 p-4">
              <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-violet-300" /><span className="tech-label">RESPONSE · CITATIONS ENFORCED</span></div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-400">{["Obligation", "Regulator", "Source", "Function", "Risk", "Owner", "Due", "Status", "Escalation"].map((h) => <th key={h} className="px-2 py-2 font-mono uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead>
                  <tbody>
                    {m.table.map((r) => (
                      <tr key={r.obligation} className="border-t border-white/5">
                        <td className="px-2 py-2 text-slate-200">{r.obligation}</td>
                        <td className="px-2 py-2 text-slate-300">{r.regulator}</td>
                        <td className="px-2 py-2 text-cyan-300 font-mono">{r.source}</td>
                        <td className="px-2 py-2 text-slate-300">{r.func}</td>
                        <td className="px-2 py-2"><span className={`text-[10px] font-mono ${r.risk === "High" ? "text-rose-300" : "text-amber-300"}`}>{r.risk}</span></td>
                        <td className="px-2 py-2 text-slate-300">{r.owner}</td>
                        <td className="px-2 py-2 font-mono">{r.due}</td>
                        <td className="px-2 py-2 text-slate-300">{r.status}</td>
                        <td className="px-2 py-2 text-violet-300">{r.escalate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-[11px] font-mono text-slate-500">{m.note || "Response requires Company Secretary review before dissemination."}</div>
            </div>
          ) : (
            <div key={i} className="rounded-md border border-violet-400/20 bg-violet-500/5 p-4" data-testid={`board-copilot-answer-${i}`}>
              <div className="flex items-center gap-2 mb-2"><Sparkles size={14} className="text-violet-300" /><span className="tech-label">RESPONSE</span><span className="ml-auto text-[10px] text-slate-500 font-mono">Conf. {(m.confidence * 100 || 0).toFixed(0)}% · {m.refreshed}</span></div>
              <div className="text-sm text-slate-100 leading-relaxed">{m.root}</div>
              {m.metrics?.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {m.metrics.map((k) => <div key={k.k} className="bg-black/30 border border-white/5 rounded p-2"><div className="tech-label text-[9px]">{k.k}</div><div className="text-sm text-white mt-0.5 font-mono">{k.v}</div></div>)}
                </div>
              )}
              {m.actions?.length > 0 && (
                <div className="mt-3">
                  <div className="tech-label">RECOMMENDED ACTIONS</div>
                  <ul className="mt-1 text-sm text-slate-200 space-y-1">{m.actions.map((a) => <li key={a}>→ {a}</li>)}</ul>
                </div>
              )}
              {m.impact && m.impact !== "-" && <div className="mt-3 text-sm text-violet-300">Estimated impact: <span className="font-mono">{m.impact}</span></div>}
              {m.sources?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{m.sources.map((s) => <span key={s} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-400">{s}</span>)}</div>}
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={loading ? "Thinking…" : "Ask about obligations, risk, audit or governance..."} disabled={loading} className="flex-1 rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-violet-400/40 disabled:opacity-60" data-testid="board-copilot-input" />
          <button onClick={() => send()} disabled={loading} className="px-4 py-2 rounded-md text-sm btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="board-copilot-send"><Send size={14} /> {loading ? "…" : "Ask"}</button>
        </div>
      </div>
      <div className="glass rounded-xl p-5">
        <div className="tech-label mb-3">SUGGESTED QUERIES</div>
        {["Which regulatory obligations are due this quarter?", "Which audit findings are repeat and overdue?", "Which risks have breached KRI thresholds?", "Which actions require board approval?", "Summarize Q1 board pack in five points"].map((q) => (
          <button key={q} onClick={() => send(q)} disabled={loading} className="w-full text-left text-sm text-slate-300 hover:text-violet-200 border border-white/5 hover:border-violet-400/30 rounded-md p-3 mb-2 transition-colors disabled:opacity-50" data-testid={`board-suggest-${q.slice(0, 10)}`}>{q}</button>
        ))}
      </div>
    </div>
  );
}

function DocumentSearch() {
  const [q, setQ] = useState("");
  const docs = [
    { name: "OSFI Capital Adequacy Guideline v4.3", type: "Regulatory", pages: 142, updated: "2026-01-22" },
    { name: "Q4 2025 Audit Committee Pack", type: "Board", pages: 88, updated: "2025-12-10" },
    { name: "AML Monitoring Model Documentation", type: "Policy", pages: 34, updated: "2025-09-04" },
    { name: "Third-Party Risk Framework", type: "Framework", pages: 61, updated: "2026-01-11" },
    { name: "Climate Risk Disclosure Discussion Paper", type: "Regulatory", pages: 27, updated: "2025-12-19" },
    { name: "Board Charter 2026", type: "Governance", pages: 18, updated: "2026-01-04" },
  ];
  const filtered = docs.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()) || d.type.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="glass rounded-xl p-4 flex gap-2 items-center"><Search size={16} className="text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search circulars, board packs, policies, frameworks..." className="flex-1 bg-transparent focus:outline-none text-sm text-slate-100" data-testid="doc-search-input" /></div>
      <div className="grid md:grid-cols-2 gap-3">
        {filtered.map((d) => (
          <div key={d.name} className="glass rounded-xl p-4 flex items-start gap-3">
            <FileText size={18} className="text-cyan-300 mt-1" />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">{d.name}</div>
              <div className="text-[11px] text-slate-400 font-mono mt-1">{d.type.toUpperCase()} · {d.pages} PAGES · UPDATED {d.updated}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalWorkflow() {
  const items = [
    { name: "Q1 Board Pack — MapleTrust", owner: "Company Secretary", stage: "Final approval · CFO", eta: "2h" },
    { name: "AML threshold recalibration", owner: "Compliance Head", stage: "Risk Committee sign-off", eta: "1d" },
    { name: "Climate disclosure baseline", owner: "CFO Office", stage: "Board endorsement", eta: "1w" },
    { name: "Cyber incident disclosure draft", owner: "General Counsel", stage: "Legal review", eta: "6h" },
  ];
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.name} className="glass rounded-xl p-5 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[240px]"><div className="font-heading text-white tracking-tight">{it.name}</div><div className="text-xs text-slate-400 mt-1">Owner · {it.owner}</div></div>
          <div className="text-xs text-slate-300"><span className="tech-label mr-2">STAGE</span>{it.stage}</div>
          <div className="text-xs text-cyan-300 font-mono">ETA {it.eta}</div>
          <div className="flex gap-2">
            <button onClick={() => toast.success(`${it.name} approved`)} className="px-3 py-1.5 rounded-md text-xs btn-primary" data-testid={`approve-${it.name.slice(0, 10)}`}>Approve</button>
            <button onClick={() => toast(`${it.name} sent back`, { description: "Requester notified" })} className="px-3 py-1.5 rounded-md text-xs btn-ghost">Return</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const MODULES = { board: BoardDashboard, pack: BoardPackBuilder, regulatory: RegulatoryIntel, audit: AuditIntel, governance: GovernanceActionsView, risk: RiskHeatmap, copilot: ExecutiveCopilot, search: DocumentSearch, approvals: ApprovalWorkflow };

export default function DemoBoard() {
  const [active, setActive] = useState("board");
  const Comp = MODULES[active];
  const meta = MENU.find((m) => m.key === active);
  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <div className="pt-16 flex min-h-screen">
        <aside className="hidden lg:flex flex-col w-64 border-r border-white/5 bg-[#0B1021]/80 backdrop-blur-xl fixed left-0 top-16 bottom-0" data-testid="board-sidebar">
          <div className="p-5 border-b border-white/5">
            <div className="tech-label">PRODUCT</div>
            <div className="font-heading text-white mt-1 tracking-tight">Board Intelligence</div>
            <div className="tech-label mt-2 text-[9px] text-slate-500">TENANT · MAPLETRUST-BANK</div>
          </div>
          <nav className="p-3 flex-1 space-y-1">
            {MENU.map((m) => (
              <button key={m.key} onClick={() => setActive(m.key)} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors ${active === m.key ? "bg-violet-500/10 border border-violet-400/30 text-violet-100" : "text-slate-300 hover:bg-white/[0.03] border border-transparent"}`} data-testid={`board-sidebar-${m.key}`}>
                <m.icon size={15} />
                {m.label}
              </button>
            ))}
          </nav>
          <div className="p-4 text-[10px] font-mono text-slate-500 border-t border-white/5">SEC_LVL_9 · ENCRYPTED<br />ENV · PROD · CA-CENTRAL-1<br />BUILD v4.2.1</div>
        </aside>
        <main className="lg:ml-64 flex-1 p-6 lg:p-8">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <meta.icon size={18} className="text-violet-300" />
            <h1 className="font-heading text-2xl text-white tracking-tight">{meta.label}</h1>
            <span className="ml-auto text-[10px] font-mono text-slate-500 border border-white/10 rounded px-2 py-1">{SYNTHETIC_DISCLAIMER}</span>
          </div>
          <div className="lg:hidden flex overflow-x-auto gap-2 pb-3 mb-4">
            {MENU.map((m) => (
              <button key={m.key} onClick={() => setActive(m.key)} className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs border ${active === m.key ? "bg-violet-500/10 border-violet-400/30 text-violet-200" : "border-white/10 text-slate-300"}`}>{m.label}</button>
            ))}
          </div>
          <Comp />
        </main>
      </div>
    </div>
  );
}
